import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { memo } from 'react';

/**
 * SEOHead Component
 * Renders dynamic meta tags, structured data, and SEO elements
 * Now supports multilingual SEO for international discoverability
 */
const SEOHead = memo(function SEOHead({
  title,
  description,
  keywords,
  canonical,
  image,
  type = 'website',
  structuredData = null,
  breadcrumbs = null,
  noindex = false,
  nofollow = false,
  hreflang = ['en', 'es-MX', 'fr-CA', 'pt-BR', 'ru', 'zh-CN'],
  announcement = null, // Deep link announcement object
}) {
  const { i18n, t } = useTranslation();
  const currentLang = i18n.language || 'en';
  
  const baseUrl = import.meta.env.VITE_SITE_URL;
  const fullImage = image?.startsWith('http') ? image : `${baseUrl}${image || '/logo.png'}`;
  const fullCanonical = canonical?.startsWith('http') ? canonical : `${baseUrl}${canonical || ''}`;

  // Use translated SEO metadata as fallbacks
  const defaultTitle = t('seo.title', 'TrckIt - Track Food Trucks & Mobile Vendors');
  const defaultDescription = t('seo.description', 'Track and find mobile vendors like food trucks, ice cream vendors, and mobile services in your area with TrckIt');
  const defaultKeywords = t('seo.keywords', 'food trucks, mobile vendors, tracking, location, find nearby, ice cream truck, mobile services, street food, vendor map');

  // If announcement prop is provided, override with announcement-specific metadata
  let finalTitle = title || defaultTitle
  let finalDescription = description || defaultDescription
  let finalKeywords = keywords || defaultKeywords
  let finalCanonical = fullCanonical
  let finalType = type

  if (announcement) {
    finalTitle = `${announcement.title} - TrckIt`
    finalDescription = announcement.content?.substring(0, 160) || defaultDescription
    finalKeywords = `${announcement.announcementType}, announcement, ${defaultKeywords}`
    
    // Generate slug for deep link
    const slug = announcement.title.toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim()
    finalCanonical = `${baseUrl}/?announcement=${slug}&lang=${currentLang}`
    finalType = 'article'
  }

  // Generate robots meta
  const robotsContent = [];
  if (noindex) robotsContent.push('noindex');
  if (nofollow) robotsContent.push('nofollow');
  const robotsMeta = robotsContent.length > 0 ? robotsContent.join(', ') : 'index, follow';

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{finalTitle}</title>
      <meta name="description" content={finalDescription} />
      <meta name="keywords" content={finalKeywords} />
      <meta name="robots" content={robotsMeta} />
      <meta name="author" content="TrckIt" />
      
      {/* Canonical URL */}
      <link rel="canonical" href={finalCanonical} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={finalType} />
      <meta property="og:title" content={finalTitle} />
      <meta property="og:description" content={finalDescription} />
      <meta property="og:image" content={fullImage} />
      <meta property="og:url" content={finalCanonical} />
      <meta property="og:site_name" content="TrckIt" />
      <meta property="og:locale" content={currentLang === 'es-MX' ? 'es_MX' : currentLang === 'fr-CA' ? 'fr_CA' : currentLang === 'pt-BR' ? 'pt_BR' : 'en_US'} />
      <meta property="og:locale:alternate" content="en_US" />
      <meta property="og:locale:alternate" content="es_MX" />
      <meta property="og:locale:alternate" content="fr_CA" />
      <meta property="og:locale:alternate" content="pt_BR" />

      {/* Article-specific OG tags for announcements */}
      {announcement && (
        <>
          <meta property="article:published_time" content={announcement.publishedAt} />
          <meta property="article:author" content={announcement.authorName || 'TrckIt Team'} />
          <meta property="article:section" content={announcement.announcementType} />
        </>
      )}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={finalTitle} />
      <meta name="twitter:description" content={finalDescription} />
      <meta name="twitter:image" content={fullImage} />

      {/* HTML lang attribute */}
      <html lang={currentLang} />

      {/* Hreflang for multi-language support - indicates same content available in different languages */}
      {hreflang && hreflang.map(lang => {
        // For announcements, generate deep link with lang param
        const hreflangUrl = announcement 
          ? `${baseUrl}/?announcement=${announcement.title.toLowerCase().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-').replace(/-+/g, '-').trim()}&lang=${lang}`
          : fullCanonical
        return (
          <link
            key={lang}
            rel="alternate"
            hrefLang={lang}
            href={hreflangUrl}
          />
        )
      })}
      <link rel="alternate" hrefLang="x-default" href={finalCanonical} />
      
      {/* Geographic meta tags for better international targeting */}
      <meta name="geo.region" content="US" />
      <meta name="geo.placename" content="United States" />
      
      {/* Mobile app meta tags */}
      <meta name="mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      <meta name="apple-mobile-web-app-title" content="TrckIt" />

      {/* Structured Data - LocalBusiness, Organization, etc. */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}

      {/* Breadcrumb Structured Data */}
      {breadcrumbs && (
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbs)}
        </script>
      )}
    </Helmet>
  );
})

export default SEOHead
